/* eslint-disable jsx-a11y/label-has-associated-control */
import * as Yup from 'yup';
import React from 'react';
import { Icon } from '@iconify/react';
import { useFormik, FormikProvider, Form } from 'formik';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import saveIcon from '@iconify/icons-eva/save-outline';

// material
import { Stack, TextField, IconButton, InputAdornment } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { createWithURLSearchParams, editItem } from '../../fetch';
import { sessionActions } from '../../store';
import { useTranslation } from '../../common/LocalizationProvider';
import { selectSession } from '../../createSelectorUtils';

// ----------------------------------------------------------------------

export default function UserForm() {
  const t = useTranslation();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const session = useSelector(selectSession);

  const RegisterSchema = Yup.object().shape({
    currentPassword: Yup.string().required('Password is required'),
    newPassword: Yup.string().required('Password is required'),
    confirmNewPassword: Yup.string().required('Password is required')
  });
  const formik = useFormik({
    initialValues: {
      user: session,
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmNewPassword: false
    },
    validationSchema: RegisterSchema,
    onSubmit: async (values) => {
      if (values.newPassword === values.confirmNewPassword) {
        const responseSession = await createWithURLSearchParams(
          'session',
          `email=${session.email}&password=${values.currentPassword}`,
          'POST'
        );
        if (responseSession.ok) {
          if (values.newPassword === values.currentPassword) {
            setErrors({ newPassword: t('STG_ChangePassword_Error1') });
          } else {
            const body = { ...session };
            delete body.permission;

            const response = await editItem('users', body.id, {
              ...body,
              password: values.newPassword
            });
            if (response.ok) {
              enqueueSnackbar(t('STG_ChangePassword_Success'), {
                variant: 'success'
              });
              const result = await response.json();
              dispatch(sessionActions.update(await result));
            } else {
              const error = await response.text();
              const message = error.slice(0, error.indexOf('-'));
              enqueueSnackbar(message, { variant: 'error' });
            }
          }
        } else {
          setErrors({ currentPassword: t('') });
        }
      } else {
        setErrors({ newPassword: t('STG_ChangePassword_Error3') });
        setErrors({ confirmNewPassword: t('STG_ChangePassword_Error3') });
      }
      setSubmitting(false);
    }
  });
  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setSubmitting,
    setFieldValue,
    setErrors
  } = formik;

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            fullWidth
            autoComplete="current-password"
            type={getFieldProps('showCurrentPassword').value ? 'text' : 'password'}
            label={t('STG_ChangePassword_CurrentPassword')}
            {...getFieldProps('currentPassword')}
            value={getFieldProps('currentPassword').value || ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() =>
                      setFieldValue(
                        'showCurrentPassword',
                        !getFieldProps('showCurrentPassword').value
                      )
                    }
                  >
                    <Icon
                      icon={getFieldProps('showCurrentPassword').value ? eyeFill : eyeOffFill}
                    />
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.currentPassword && errors.currentPassword)}
            helperText={touched.currentPassword && errors.currentPassword}
          />

          <TextField
            fullWidth
            autoComplete="current-password"
            type={getFieldProps('showNewPassword').value ? 'text' : 'password'}
            label={t('STG_ChangePassword_NewPassword')}
            {...getFieldProps('newPassword')}
            value={getFieldProps('newPassword').value || ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() =>
                      setFieldValue('showNewPassword', !getFieldProps('showNewPassword').value)
                    }
                  >
                    <Icon icon={getFieldProps('showNewPassword').value ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.newPassword && errors.newPassword)}
            helperText={touched.newPassword && errors.newPassword}
          />

          <TextField
            fullWidth
            autoComplete="current-password"
            type={getFieldProps('showConfirmNewPassword').value ? 'text' : 'password'}
            label={t('STG_ChangePassword_ConfirmPassword')}
            {...getFieldProps('confirmNewPassword')}
            value={getFieldProps('confirmNewPassword').value || ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() =>
                      setFieldValue(
                        'showConfirmNewPassword',
                        !getFieldProps('showConfirmNewPassword').value
                      )
                    }
                  >
                    <Icon
                      icon={getFieldProps('showConfirmNewPassword').value ? eyeFill : eyeOffFill}
                    />
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.confirmNewPassword && errors.confirmNewPassword)}
            helperText={touched.confirmNewPassword && errors.confirmNewPassword}
          />

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            startIcon={<Icon icon={saveIcon} />}
          >
            {t('btn_Save')}
          </LoadingButton>
        </Stack>
      </Form>
    </FormikProvider>
  );
}
